/** Reachability (plan "Page anatomy" → Reachability): can we reach this person? One compact
 *  row per channel — the channel, its verdict in icon and words with the reason, and under
 *  it the address delivery actually uses (read-only, with its value actions) and "Contact ›"
 *  to that contact's row in the Contacts card; the notifications opt-in at the row's end.
 *  Only the user's own email and phone count; delivery never uses secondary contacts.
 *
 *  The opt-ins are the registry's `reachability` fields — FieldSlots ("Opted in", Yes / No),
 *  a switch in edit mode on the same geometry; the slot reserves its height in both modes,
 *  and the verdict and address lines sit beside it, so Edit moves nothing. While a field the
 *  verdict depends on is changed in the draft (the opt-in, the contacts) the verdict says
 *  "updates after save" — the verdict is the server's, never re-derived here. What the app
 *  reports (consents, device notifications) is the muted "From the app" block. */
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Link, Typography, type SvgIconProps } from '@mui/material';
import type { ComponentType, ReactNode } from 'react';

import type { UserRecordSection } from '@/api/userRecord';
import type { ConsoleUserDetail, ReachabilityChannel, ReachabilityVerdict } from '@/api/users';
import { SectionCard } from '@/components/ReadView';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { recordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { asContactsValue } from '@/features/records/fields/widgets/contactsModel';
import { BlockRule } from '@/features/records/panels/BlockRule';
import type { ViewSection } from '@/features/records/views/types';

import { emailActions, gridKindOf, phoneActions, type UserCtx } from '../userLinks';

import {
  CHANNEL_LABEL,
  channelAddress,
  channelContact,
  REACHABILITY_CHANNELS,
  verdictText,
  type ChannelAddress,
  type ChannelContact,
} from './reachabilityModel';
import { revealElement } from './reveal';
import { userSectionAnchorId } from './views/userLayout';

export const REACHABILITY_ANCHOR_ID = userSectionAnchorId('reachability');
const CONTACTS_ANCHOR_ID = userSectionAnchorId('contacts');

/** The height of a FieldSlot's label row: the channel and the verdict's first line sit level
 *  with "Opted in". */
const LABEL_ROW_PX = 24;
/** The verdict icon and its gap: the address line starts under the verdict's words. */
const VERDICT_INDENT_PX = 22;

/** Scroll to the contact's row in the Contacts card and focus it (the card itself when the
 *  row is not there). */
function revealContact(target: ChannelContact) {
  const row =
    target.kind === 'row' ? `[data-contact="${target.key}"]` : `[data-contact-missing="${target.type}"]`;
  const el =
    document.querySelector<HTMLElement>(`#${CONTACTS_ANCHOR_ID} ${row}`) ?? document.getElementById(CONTACTS_ANCHOR_ID);
  if (el) revealElement(el, 'center');
}

/** Each channel's opt-in field, and the draft paths its verdict depends on. */
const OPT_IN: Partial<Record<ReachabilityChannel, string>> = {
  email: 'allow_email_nf',
  sms: 'allow_sms_nf',
  whatsapp: 'allow_whatsapp_nf',
};
const DEPENDS_ON: Record<ReachabilityChannel, string[]> = {
  email: ['allow_email_nf', 'contacts'],
  sms: ['allow_sms_nf', 'contacts'],
  whatsapp: ['allow_whatsapp_nf', 'contacts'],
  push: [],
  in_app_chat: [],
};

const CONSENTS: Array<[keyof ConsoleUserDetail['attributes']['consents'], string]> = [
  ['privacy_policy', 'Privacy policy'],
  ['email', 'Email'],
  ['sms', 'SMS'],
  ['whatsapp', 'WhatsApp'],
  ['push', 'Push'],
  ['airbnb', 'Airbnb'],
  ['ads', 'Ads'],
];

const CHANNEL_ICON: Record<ReachabilityChannel, ComponentType<SvgIconProps>> = {
  email: MailOutlineIcon,
  sms: SmsOutlinedIcon,
  whatsapp: WhatsAppIcon,
  push: NotificationsNoneIcon,
  in_app_chat: ChatBubbleOutlineIcon,
};

/** The verdict's icon and colour: ink for "will receive", the `error` icon for a gate that
 *  blocks (the words stay ink), muted for what cannot be confirmed. */
const VERDICT: Record<ReachabilityVerdict, { icon: ComponentType<SvgIconProps>; iconColor: string; textColor: string }> = {
  will_receive: { icon: CheckCircleOutlineIcon, iconColor: 'text.primary', textColor: 'text.primary' },
  blocked: { icon: HighlightOffIcon, iconColor: 'error.main', textColor: 'text.primary' },
  unconfirmed: { icon: HelpOutlineIcon, iconColor: 'text.secondary', textColor: 'text.secondary' },
};

function Address({ address, ctx, conversation }: { address: ChannelAddress; ctx: UserCtx; conversation: string | null }) {
  const { value, kind, note } = address;
  let main: ReactNode = null;
  if (value && kind === 'email') main = <ValueActions actions={emailActions(value, ctx)}>{value}</ValueActions>;
  else if (value && kind === 'phone') main = <ValueActions actions={phoneActions(value, ctx)}>{value}</ValueActions>;
  else if (value && conversation) {
    main = <ValueActions actions={[act.copy(conversation, 'Copy the conversation name')]}>{value}</ValueActions>;
  } else if (value) main = value;
  return (
    <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }} data-reach-address>
      {main}
      {note && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {main ? ' · ' : ''}
          {note}
        </Box>
      )}
    </Box>
  );
}

function consentWords(user: ConsoleUserDetail): { given: string[]; not: string[] } {
  const c = user.attributes.consents;
  return {
    given: CONSENTS.filter(([k]) => c[k]).map(([, l]) => l),
    not: CONSENTS.filter(([k]) => !c[k]).map(([, l]) => l),
  };
}

function deviceNotifications(v: boolean | null): string {
  return v === null ? 'not reported by the app' : v ? 'on' : 'off';
}

export function ReachabilityCard({
  user,
  section,
}: {
  user: ConsoleUserDetail;
  /** The registry's `reachability` card (its opt-in fields). */
  section?: ViewSection<UserRecordSection>;
}) {
  const ctx: UserCtx = { tenantId: user.tour_operator?.id ?? null, kind: gridKindOf(user.kind) };
  const mainOffice = user.reachability.in_app_chat.main_office;
  const { mode } = useRecordEditor();
  const { state, baselineOf } = useDraft();
  const rec = recordKey('user', user.id);
  // The stored contacts (what the Contacts card lists by these keys); none without them.
  const storedContacts = baselineOf(rec, 'contacts');
  const contacts = storedContacts === undefined ? null : asContactsValue(storedContacts);
  const placed = new Set((section?.blocks ?? []).flatMap((b) => b.fields.map((f) => f.key)));
  const dirty = (ch: ReachabilityChannel) =>
    mode === 'edit' && DEPENDS_ON[ch].some((path) => state.entries[rec]?.[path] !== undefined);
  const consents = consentWords(user);
  return (
    <Box
      component="section"
      id={REACHABILITY_ANCHOR_ID}
      tabIndex={-1}
      aria-label="Reachability"
      data-reachability
      data-stage-panel="reachability"
      sx={{
        scrollMarginTop: 'var(--record-spine-height, 0px)',
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard
        title="Reachability"
        action={
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
            Delivery uses the user’s own email and phone only
          </Typography>
        }
      >
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {REACHABILITY_CHANNELS.map((ch, i) => {
            const entry = user.reachability[ch];
            const v = VERDICT[entry.verdict];
            const Icon = CHANNEL_ICON[ch];
            const VerdictIcon = v.icon;
            const optIn = OPT_IN[ch];
            const address = channelAddress(user, ch);
            const target = contacts ? channelContact(user, ch, contacts) : null;
            const hasAddress = Boolean(address.value || address.note);
            return (
              <Box
                component="li"
                key={ch}
                data-channel={ch}
                data-verdict={entry.verdict}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: '112px minmax(0, 1fr) auto' },
                  // A phone: the verdict under the channel's name, the opt-in beside both.
                  gridTemplateAreas: { xs: '"channel optin" "facts optin"', sm: '"channel facts optin"' },
                  alignItems: 'start',
                  columnGap: 2,
                  rowGap: 0.25,
                  py: 1,
                  borderTop: i === 0 ? 0 : 1,
                  borderColor: 'divider',
                  typography: 'body2',
                }}
              >
                <Box
                  sx={{
                    gridArea: 'channel',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minHeight: LABEL_ROW_PX,
                    fontWeight: 600,
                  }}
                >
                  <Icon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {CHANNEL_LABEL[ch]}
                </Box>
                <Box sx={{ gridArea: 'facts', minWidth: 0, display: 'grid', gap: 0.25 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, pt: '2px', color: v.textColor }}
                    data-verdict-line
                  >
                    <VerdictIcon aria-hidden sx={{ fontSize: 16, mt: '2px', color: v.iconColor, flexShrink: 0 }} />
                    <span>
                      {verdictText(entry)}
                      {dirty(ch) && (
                        <Box component="span" data-updates-after-save sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {' · '}
                          <SyncIcon aria-hidden sx={{ fontSize: 13, verticalAlign: '-2px' }} /> updates after save
                        </Box>
                      )}
                    </span>
                  </Box>
                  {hasAddress && (
                    <Box
                      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1, pl: `${VERDICT_INDENT_PX}px`, minWidth: 0 }}
                      data-address-line
                    >
                      <Address
                        address={address}
                        ctx={ctx}
                        conversation={ch === 'in_app_chat' ? (mainOffice?.unique_name ?? null) : null}
                      />
                      {target && (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          underline="hover"
                          onClick={() => revealContact(target)}
                          aria-label={`Show ${address.value ?? CHANNEL_LABEL[ch]} in Contacts`}
                          sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                          data-contact-link={target.kind === 'row' ? target.key : `missing:${target.type}`}
                        >
                          Contact ›
                        </Link>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ gridArea: 'optin', minWidth: 0 }} data-opt-in>
                  {optIn && placed.has(optIn) && (
                    <FieldSlot
                      fieldKey={optIn}
                      entity="user"
                      id={user.id}
                      label="Opted in"
                      display={user[optIn as 'allow_email_nf'] ? 'Yes' : 'No'}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        <BlockRule icon={<SyncIcon aria-hidden sx={{ fontSize: 14 }} />} title="From the app" note="read-only">
          <Box sx={{ display: 'grid', gap: 0.25, typography: 'body2', color: 'text.secondary' }} data-app-facts>
            <span>
              Consents given: {consents.given.length > 0 ? consents.given.join(', ') : 'none'}
              {consents.not.length > 0 ? ` · not given: ${consents.not.join(', ')}` : ''}
            </span>
            <span>Device notifications: {deviceNotifications(user.attributes.notifications_status)}</span>
          </Box>
        </BlockRule>
      </SectionCard>
    </Box>
  );
}
